// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/install/InstallAppManager.tsx
// 🔧 PURPOSE: Quando proporre "Installa l'app" da sola (montato una volta in
//   App), al posto dei vecchi banner di index.tsx che uscivano subito, sopra
//   tutto e ogni 60 secondi.
//   - Due occasioni: subito dopo l'accesso con la prenotazione, e dopo 20
//     secondi di uso per tutti; la prima che arriva.
//   - Solo su pagine tranquille (Home, Esplora, Partner…): mai sopra
//     check-in, pagamenti e link speciali; se l'occasione arriva lì, aspetta.
//   - "Non ora" = 3 giorni; al massimo 3 volte; "L'ho già installata" = mai.
//   - Non su schermi grandi (lì c'è DesktopGate) né se l'app è già installata.
//   Il riquadro in Home (InstallAppCard) apre lo stesso foglio quando si vuole.
// ============================================

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useGuestSession } from '../../hooks/useGuestSession';
import {
  canAutoShowInstall,
  countAutoShow,
  isCalmRoute,
  neverInstall,
  OPEN_INSTALL_EVENT,
  snoozeInstall,
} from '../../lib/installApp';
import InstallAppSheet, { type InstallCloseReason } from './InstallAppSheet';
import { useInstallApp } from './useInstallApp';

export const AUTO_DELAY_MS = 20_000;
export const AFTER_LOGIN_DELAY_MS = 4_000;
const DESKTOP_MIN_WIDTH = 1024;

const InstallAppManager: React.FC = () => {
  const { pathname, search } = useLocation();
  const { isLoggedIn } = useGuestSession();
  const { installed, guide } = useInstallApp();
  const [open, setOpen] = useState(false);
  const [due, setDue] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setDue(true), AUTO_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return undefined;
    const timer = window.setTimeout(() => setDue(true), AFTER_LOGIN_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [isLoggedIn]);

  useEffect(() => {
    if (!due || open) return;
    if (!isCalmRoute(pathname, search)) return; // aspetta una pagina tranquilla
    setDue(false);
    if (installed || guide.kind === 'none' || window.innerWidth >= DESKTOP_MIN_WIDTH || !canAutoShowInstall()) return;
    countAutoShow();
    setOpen(true);
  }, [due, open, pathname, search, installed, guide.kind]);

  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener(OPEN_INSTALL_EVENT, show);
    return () => window.removeEventListener(OPEN_INSTALL_EVENT, show);
  }, []);

  const close = (reason: InstallCloseReason) => {
    setOpen(false);
    if (reason === 'later') snoozeInstall();
    if (reason === 'never') neverInstall();
  };

  if (installed && !open) return null;
  return <InstallAppSheet open={open} guide={guide} loggedIn={isLoggedIn} onClose={close} />;
};

export default InstallAppManager;
